import { useCallback, useEffect, useRef, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Stack,
} from "@mui/material";
import moment from "moment";
import { axiosGet, axiosPostWithJson } from "../../util/axios";
import { useDispatch } from "react-redux";
import { IRootThunkDispatch } from "../../redux/store";
import {
    OpenSnackBarWithResultFail,
    OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";

interface Props {
    open: boolean;
    userId: number;
    username: string;
    onClose: () => void;
}

interface LogRequestRow {
    app_log_request_id: number;
    requested_date: string;
    status: string;
    s3_key: string | null;
    requested_at: string;
    fulfilled_at: string | null;
    error_message: string | null;
}

const POLL_MS = 5000;

function formatMs(ms: string | null) {
    if (!ms) return "-";
    const n = parseInt(ms, 10);
    if (isNaN(n)) return ms;
    return moment(n).format("YYYY-MM-DD HH:mm:ss");
}

const statusLabel: Record<string, string> = {
    pending: "等待 App 上傳",
    done: "已完成",
    not_available: "該日無檔案",
    failed: "上傳失敗",
};

function AppLogDialog({ open, userId, username, onClose }: Props) {
    const dispatch = useDispatch<IRootThunkDispatch>();
    const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
    const [list, setList] = useState<LogRequestRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [polling, setPolling] = useState(false);
    const prevStatusRef = useRef<Record<number, string>>({});
    const silentRef = useRef(false);

    const minDate = moment().subtract(7, "days").format("YYYY-MM-DD");
    const maxDate = moment().format("YYYY-MM-DD");

    const notifyStatusChanges = useCallback(
        (rows: LogRequestRow[]) => {
            const prev = prevStatusRef.current;
            for (const row of rows) {
                const id = row.app_log_request_id;
                const before = prev[id];
                const after = row.status;
                if (before === "pending" && after && after !== "pending") {
                    if (after === "done") {
                        dispatch(
                            OpenSnackBarWithResultSuccess(
                                `日誌已上傳：${row.requested_date}（#${id}），可以下載`,
                            ),
                        );
                    } else if (after === "not_available") {
                        dispatch(
                            OpenSnackBarWithResultFail(
                                `機上找不到 ${row.requested_date} 的日誌（#${id}）`,
                            ),
                        );
                    } else if (after === "failed") {
                        dispatch(
                            OpenSnackBarWithResultFail(
                                `日誌上傳失敗：${row.requested_date}（#${id}）`,
                            ),
                        );
                    }
                }
            }
            const next: Record<number, string> = {};
            for (const row of rows) {
                next[row.app_log_request_id] = row.status;
            }
            prevStatusRef.current = next;
        },
        [dispatch],
    );

    const loadList = useCallback(
        async (opts?: { silent?: boolean }) => {
            const silent = !!opts?.silent;
            if (!silent) setLoading(true);
            try {
                const result = await axiosGet(
                    `/api/app-log/list?user_id=${userId}&app_type=cleaning`,
                );
                if (result.data.success) {
                    const rows: LogRequestRow[] = result.data.list || [];
                    notifyStatusChanges(rows);
                    setList(rows);
                    setPolling(rows.some((r) => r.status === "pending"));
                } else if (!silent) {
                    dispatch(OpenSnackBarWithResultFail("未能取得日誌請求列表"));
                }
            } catch {
                if (!silent) {
                    dispatch(OpenSnackBarWithResultFail("未能取得日誌請求列表"));
                }
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [userId, dispatch, notifyStatusChanges],
    );

    useEffect(() => {
        if (open && userId) {
            prevStatusRef.current = {};
            loadList();
        } else {
            setPolling(false);
        }
    }, [open, userId, loadList]);

    // Auto-poll while dialog open and any pending request
    useEffect(() => {
        if (!open || !polling) return;
        const timer = window.setInterval(() => {
            if (silentRef.current) return;
            silentRef.current = true;
            loadList({ silent: true }).finally(() => {
                silentRef.current = false;
            });
        }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [open, polling, loadList]);

    async function handleRequest() {
        const requested_date = moment(date).format("YYYYMMDD");
        setSubmitting(true);
        try {
            const result = await axiosPostWithJson("/api/app-log/request", {
                user_id: userId,
                requested_date,
                app_type: "cleaning",
            });
            if (result.data.success) {
                dispatch(
                    OpenSnackBarWithResultSuccess(
                        "已建立請求，等待 App 上傳中…（會自動檢查）",
                    ),
                );
                await loadList();
            } else {
                dispatch(
                    OpenSnackBarWithResultFail(
                        result.data.message || "建立請求失敗",
                    ),
                );
            }
        } catch {
            dispatch(OpenSnackBarWithResultFail("建立請求失敗"));
        } finally {
            setSubmitting(false);
        }
    }

    async function handleDownload(id: number) {
        try {
            const result = await axiosGet(`/api/app-log/download/${id}`);
            if (result.data.success && result.data.url) {
                window.open(result.data.url, "_blank");
            } else {
                dispatch(
                    OpenSnackBarWithResultFail(
                        result.data.message || "下載失敗",
                    ),
                );
            }
        } catch {
            dispatch(OpenSnackBarWithResultFail("下載失敗"));
        }
    }

    const pendingCount = list.filter((r) => r.status === "pending").length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>App 日誌 — {username} (#{userId})</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    選擇日子後按「要求上傳」。前線 App
                    下次有網／開機時會自動上傳該日 log（保留最近 7 日）。
                    有「等待中」時會每 {POLL_MS / 1000}{" "}
                    秒自動檢查，完成會彈出提示。
                </Typography>
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                >
                    <TextField
                        type="date"
                        label="日誌日期"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={date}
                        inputProps={{ min: minDate, max: maxDate }}
                        onChange={(e) => setDate(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        onClick={handleRequest}
                        disabled={submitting}
                    >
                        {submitting ? "提交中…" : "要求上傳"}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => loadList()}
                        disabled={loading}
                        sx={{ color: "#fff" }}
                    >
                        重新整理
                    </Button>
                    {pendingCount > 0 ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                等待 App 上傳中（{pendingCount}
                                ）…自動檢查中
                            </Typography>
                        </Stack>
                    ) : null}
                </Stack>

                {loading && list.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center" }}>
                        <CircularProgress size={28} />
                    </div>
                ) : (
                    <Table size="small" sx={{ mt: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>日子</TableCell>
                                <TableCell>狀態</TableCell>
                                <TableCell>請求時間</TableCell>
                                <TableCell>完成時間</TableCell>
                                <TableCell>操作</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        暫無請求記錄
                                    </TableCell>
                                </TableRow>
                            ) : (
                                list.map((row) => (
                                    <TableRow key={row.app_log_request_id}>
                                        <TableCell>
                                            {row.app_log_request_id}
                                        </TableCell>
                                        <TableCell>
                                            {row.requested_date}
                                        </TableCell>
                                        <TableCell>
                                            {statusLabel[row.status] ||
                                                row.status}
                                            {row.error_message
                                                ? ` (${row.error_message})`
                                                : ""}
                                        </TableCell>
                                        <TableCell>
                                            {formatMs(row.requested_at)}
                                        </TableCell>
                                        <TableCell>
                                            {formatMs(row.fulfilled_at)}
                                        </TableCell>
                                        <TableCell>
                                            {row.status === "done" ? (
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        handleDownload(
                                                            row.app_log_request_id,
                                                        )
                                                    }
                                                >
                                                    下載
                                                </Button>
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>關閉</Button>
            </DialogActions>
        </Dialog>
    );
}

export default AppLogDialog;
